import React, { useEffect, useState } from 'react';
import ModalPrevio from './ModalPrevio';

/**
 * La línea de tiempo del trámite (#82).
 *
 * Todos los estados en el orden del proceso: lo que ya pasó con check, el paso actual resaltado
 * y lo que falta en gris. Lo que no se hizo y el trámite ya dejó atrás (un FAD, un previo, una
 * inspección) no viene: lo quita el servidor (ver HistorialPortal en el backend).
 *
 *  - SIN fechas. El cliente quiere saber por dónde va lo suyo, no cronometrar a WF.
 *  - "estado" de cada paso: HECHO, ACTUAL, EN_CURSO (un previo abierto en paralelo) o PENDIENTE.
 *    Si no viene (servidor viejo, durante un despliegue), se asume lo de antes: todo hecho y el
 *    último es el actual.
 *
 * No pide nada al servidor: la línea viene con el trámite en la misma lista.
 */

const pasosDe = (tramite) => {
  const lista = tramite.historial && tramite.historial.length > 0
    ? tramite.historial
    : [tramite.estado].filter(Boolean).map((texto) => ({ texto, previoId: null }));
  const conEstado = lista.some((p) => p.estado);
  return lista.map((p, i) => ({
    ...p,
    estado: conEstado ? p.estado : (i === lista.length - 1 ? 'ACTUAL' : 'HECHO'),
  }));
};

const COLOR = {
  HECHO: 'var(--verde)',
  ACTUAL: 'var(--azul-medio)',
  EN_CURSO: 'var(--azul-medio)',
  PENDIENTE: 'var(--gris-borde)',
};

function Marca({ estado }) {
  const base = {
    width: 22, height: 22, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 900, lineHeight: 1,
  };
  if (estado === 'HECHO') {
    return <span aria-label="Listo" style={{ ...base, background: 'var(--verde)', color: '#fff' }}>✓</span>;
  }
  if (estado === 'ACTUAL') {
    return <span aria-label="Ahora" style={{
      ...base, background: 'var(--azul-medio)', boxShadow: '0 0 0 5px var(--tinte-azul)',
    }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} /></span>;
  }
  if (estado === 'EN_CURSO') {
    return <span aria-label="En curso" style={{
      ...base, border: '2px dashed var(--azul-medio)', color: 'var(--azul-medio)', fontSize: 11,
    }}>…</span>;
  }
  return <span aria-label="Pendiente" style={{ ...base, border: '2px solid var(--gris-borde)', background: 'var(--blanco)' }} />;
}

export default function ModalHistorial({ tramite, alCerrar }) {
  // Cerrar con Escape: la ventana tapa la tabla y hay que poder salir sin buscar el botón.
  useEffect(() => {
    const alTeclear = (e) => { if (e.key === 'Escape') alCerrar(); };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [alCerrar]);

  // El paso de un previo ya terminado trae su id: ahí se abre el detalle completo.
  const [previoAbierto, setPrevioAbierto] = useState(null);

  const pasos = pasosDe(tramite);
  const hechos = pasos.filter((p) => p.estado === 'HECHO' || p.estado === 'ACTUAL').length;
  // Terminado = no queda nada por hacer. Solo se puede saber si el servidor manda los estados:
  // con uno viejo todo parece hecho y diría "completado" a un trámite a medio camino.
  const conEstados = (tramite.historial || []).some((p) => p.estado);
  const terminado = conEstados && pasos.length > 0
    && pasos.every((p) => p.estado === 'HECHO' || p.estado === 'ACTUAL');
  const porcentaje = pasos.length ? Math.round((hechos / pasos.length) * 100) : 0;

  return (
    <div
      onClick={alCerrar}
      style={{
        position: 'fixed', inset: 0, zIndex: 100, padding: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(15, 23, 42, 0.55)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="tarjeta"
        style={{
          width: '100%', maxWidth: 520, maxHeight: '84vh',
          display: 'flex', flexDirection: 'column', boxShadow: 'var(--sombra-md)',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gris-borde)' }}>
          <div style={{ fontSize: 15.5, fontWeight: 800 }}>Avance del trámite</div>
          <div style={{ fontSize: 12.5, color: 'var(--gris-texto)', marginTop: 2 }}>
            {tramite.documentoAsociado}
          </div>
          {pasos.length > 1 && (
            <div style={{ marginTop: 12 }}>
              <div style={{
                height: 8, borderRadius: 999, background: 'var(--gris-claro)', overflow: 'hidden',
                border: '1px solid var(--gris-borde)',
              }}>
                <div style={{
                  width: `${porcentaje}%`, height: '100%', borderRadius: 999,
                  background: terminado ? 'var(--verde)' : 'linear-gradient(90deg, var(--verde), var(--azul-medio))',
                  transition: 'width .4s ease',
                }} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--gris-texto)', marginTop: 5, fontWeight: 600 }}>
                {terminado ? '✅ Trámite completado' : `${hechos} de ${pasos.length} pasos completados`}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '18px 20px', overflowY: 'auto', flex: 1 }}>
          {pasos.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--gris-texto)', fontSize: 13.5 }}>
              Todavía no hay estados para mostrar.
            </div>
          )}

          {pasos.map((paso, i) => {
            const esUltimo = i === pasos.length - 1;
            const siguiente = pasos[i + 1];
            // El hilo que baja al paso siguiente va verde mientras el camino ya se recorrió.
            const hiloHecho = paso.estado === 'HECHO' && siguiente
              && (siguiente.estado === 'HECHO' || siguiente.estado === 'ACTUAL');
            const pendiente = paso.estado === 'PENDIENTE';
            const actual = paso.estado === 'ACTUAL';
            return (
              // La lista es la del servidor y no cambia de orden: el índice alcanza como llave.
              <div key={i} data-estado={paso.estado} style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 22 }}>
                  {/* Terminado, el último también lleva check: ya no hay "ahora", está completo. */}
                  <Marca estado={actual && terminado ? 'HECHO' : paso.estado} />
                  {!esUltimo && (
                    <span style={{
                      flex: 1, width: hiloHecho ? 3 : 2, marginTop: 3, marginBottom: 3, minHeight: 10,
                      background: hiloHecho ? COLOR.HECHO : 'transparent',
                      borderLeft: hiloHecho ? 'none' : `2px dashed ${COLOR.PENDIENTE}`,
                    }} />
                  )}
                </div>
                <div style={{ paddingBottom: esUltimo ? 0 : 14, paddingTop: 2, minWidth: 0 }}>
                  <div style={{
                    fontSize: actual ? 14.5 : 13.5,
                    fontWeight: actual ? 800 : pendiente ? 500 : 650,
                    color: pendiente ? 'var(--gris-texto)' : 'var(--negro-texto)',
                    opacity: pendiente ? 0.75 : 1,
                  }}>
                    {paso.texto}
                  </div>
                  {actual && (
                    <div style={{ fontSize: 12, color: 'var(--azul-medio)', fontWeight: 700, marginTop: 2 }}>
                      {terminado ? 'Completado' : 'En este punto va ahora'}
                    </div>
                  )}
                  {paso.estado === 'EN_CURSO' && (
                    <div style={{ fontSize: 12, color: 'var(--azul-medio)', fontWeight: 600, marginTop: 2 }}>
                      En curso
                    </div>
                  )}
                  {paso.previoId && (
                    <button
                      type="button"
                      onClick={() => setPrevioAbierto(paso.previoId)}
                      style={{
                        marginTop: 5, padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                        border: '1px solid var(--gris-borde)', background: 'var(--gris-claro)',
                        color: 'var(--azul-medio)', fontSize: 12, fontWeight: 700,
                        fontFamily: 'inherit',
                      }}
                    >
                      Ver previo
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gris-borde)', textAlign: 'right' }}>
          <button type="button" className="btn" onClick={alCerrar}>Cerrar</button>
        </div>
      </div>

      {/* El clic dentro del previo no tiene que cerrar también el recorrido que hay detrás. */}
      {previoAbierto && (
        <div onClick={(e) => e.stopPropagation()}>
          <ModalPrevio
            previoId={previoAbierto}
            documentoAsociado={tramite.documentoAsociado}
            alCerrar={() => setPrevioAbierto(null)}
          />
        </div>
      )}
    </div>
  );
}
